import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import strings from "translate/language";
import { visitorDetail } from "reduxStore/action/visitorActions";
import {
  getAppointmentDetail,
  checkinAppointment,
  close_appointment,
  allocate_closingManager,
  remove_allocate,
  editAppointment,
  addAppointment,
} from "../../reduxStore/action/appointmentActions";
import {
  triggerWebhook,
  getSiteVisitStatusData,
  updateSiteVisitStatus,
} from "../../reduxStore/action/siteVisitActions";
import { getClosingManagerList } from "reduxStore/action/closingManagerAction";
import {
  getCPLeadType,
  handleDetailResponse,
  handleListResponse,
  seo,
} from "utils/utils";
import moment from "moment";
import { getMasterList } from "reduxStore/action/masterActions";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import {
  SupportRequest,
  ClosingManager,
  closing_head_id,
  ClosingTL,
  SourcingManager,
  SourcingTL,
  sourcing_head_id,
  SCM,
  SiteHead,
  BusinessHead,
  AdminRoleId,
  // PostSale,
  // Receptionist,
  ClusterHead,
  admin_id,
  SourcingHead,
  MIS_RoleId,
  CSTL,
} from "../../utils/constants";
import { errorToast, successToast } from "utils/httpClient";
import visitorService from "reduxStore/service/visitorService";
import Select from "react-select";
import StatusBadge, { CheckInStatusBadge } from "component/common/StatusBadge";
import {
  getClosingStatusBadgeClass,
  resolveAppointmentActionButtons,
  resolveCheckInDisplay,
  resolveClosingStatusLabel,
  shouldShowPostCheckinBookNow,
  shouldWarnClosingStatusOnBack,
} from "utils/appointmentListStatusDisplay";
import "./AppointmentListing.css";
import "component/common/JustoConfirmModal.css";

/** Set on pendingLeaveTarget when the user pressed the browser back button. */
const BROWSER_HISTORY_BACK = "__browser_history_back__";

const displayValue = (value) => {
  if (value == null || value === "" || value === "NA") return strings.notavailable;
  return value;
};
const FactCell = ({ label, value, children }) => {
  const cleanLabel = String(label || "").replace(/\s*:\s*$/, "").trim();
  return (
    <div className="justo-appointment-detail__fact">
      <dt>{cleanLabel}</dt>
      <dd className={!children && value === strings.notavailable ? "is-muted" : undefined}>
        {children || displayValue(value)}
      </dd>
    </div>
  );
};
const toTitleCase = (text = "") =>
  String(text).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const FactSection = ({ title, children }) => (
  <section className="justo-appointment-detail__section">
    <h2 className="justo-appointment-detail__section-title">{toTitleCase(title)}</h2>
    <dl className="justo-appointment-detail__facts">{children}</dl>
  </section>
);

const NO_SHOW_STATUS_OPTIONS = [
  { value: "1", label: strings.rescheduled },
  { value: "4", label: strings.appointmentCancel },
];

const ClosingStatusFormModal = ({
  open,
  titleId,
  title,
  subtitle,
  reasonOptions,
  selectedReason,
  onReasonChange,
  comment,
  onCommentChange,
  errors = {},
  submitting = false,
  confirmLabel,
  confirmDanger = false,
  onClose,
  onSubmit,
}) => {
  if (!open) return null;
  return (
    <>
      <button
        type="button"
        className="justo-appointment__modal-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        className="justo-appointment__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="justo-appointment__modal-head">
          <div>
            <h2 className="justo-appointment__modal-title" id={titleId}>
              {title}
            </h2>
            {subtitle ? (
              <p className="justo-appointment__modal-subtitle">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="justo-appointment__modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="justo-appointment__modal-body">
          <div className="justo-appointment__modal-field">
            <label className="justo-appointment__modal-label" htmlFor={`${titleId}-reason`}>
              {strings.selectReason}
            </label>
            <Select
              inputId={`${titleId}-reason`}
              classNamePrefix="justo-appointment-select"
              options={reasonOptions}
              value={selectedReason}
              onChange={onReasonChange}
              isClearable
              isSearchable
              placeholder={strings.selectReason}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10060 }),
              }}
            />
          </div>
          <div className="justo-appointment__modal-field">
            <label
              className="justo-appointment__modal-label"
              htmlFor={`${titleId}-remark`}
            >
              {strings.closingRemarkLabel}
            </label>
            <textarea
              id={`${titleId}-remark`}
              className="justo-appointment__modal-textarea"
              rows={4}
              placeholder={strings.closingRemarkPlaceholder}
              value={comment}
              onChange={onCommentChange}
            />
            {errors.comment ? (
              <p className="justo-appointment__modal-error">{errors.comment}</p>
            ) : null}
          </div>
        </div>
        <div className="justo-appointment__modal-foot">
          <button
            type="button"
            className="justo-appointment__btn justo-appointment__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {strings.cancel}
          </button>
          <button
            type="button"
            className={`justo-appointment__btn ${
              confirmDanger
                ? "justo-appointment__btn--danger"
                : "justo-appointment__btn--solid"
            }`}
            onClick={onSubmit}
            disabled={submitting}
          >
            <i className="fa fa-check" aria-hidden="true" />
            {submitting ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
};

/** App parity: No Show = Rescheduled | Appointment Cancel + mandatory description. */
const NoShowFormModal = ({
  open,
  statusValue,
  onStatusChange,
  appointmentDate,
  onDateChange,
  appointmentTime,
  onTimeChange,
  comment,
  onCommentChange,
  errors = {},
  submitting = false,
  onClose,
  onSubmit,
}) => {
  if (!open) return null;
  const selectedStatus =
    NO_SHOW_STATUS_OPTIONS.find((opt) => opt.value === String(statusValue)) ||
    null;
  const isReschedule = String(statusValue) === "1";

  return (
    <>
      <button
        type="button"
        className="justo-appointment__modal-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        className="justo-appointment__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointmentNoShowTitle"
      >
        <div className="justo-appointment__modal-head">
          <div>
            <h2 className="justo-appointment__modal-title" id="appointmentNoShowTitle">
              {strings.noShowTitle}
            </h2>
            <p className="justo-appointment__modal-subtitle">
              {strings.noShowSubtitle}
            </p>
          </div>
          <button
            type="button"
            className="justo-appointment__modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="justo-appointment__modal-body">
          <div className="justo-appointment__modal-field">
            <label
              className="justo-appointment__modal-label"
              htmlFor="appointmentNoShow-status"
            >
              {strings.status}
            </label>
            <Select
              inputId="appointmentNoShow-status"
              classNamePrefix="justo-appointment-select"
              options={NO_SHOW_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={onStatusChange}
              isClearable
              isSearchable={false}
              placeholder={strings.selectStatus || strings.selectstatus}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 10060 }),
              }}
            />
            {errors.status ? (
              <p className="justo-appointment__modal-error">{errors.status}</p>
            ) : null}
          </div>
          {isReschedule ? (
            <div className="justo-appointment__modal-row">
              <div className="justo-appointment__modal-field">
                <label
                  className="justo-appointment__modal-label"
                  htmlFor="appointmentNoShow-date"
                >
                  {strings.date}
                </label>
                <input
                  id="appointmentNoShow-date"
                  type="date"
                  className="justo-appointment__modal-input"
                  min={moment().format("YYYY-MM-DD")}
                  value={appointmentDate || ""}
                  onChange={onDateChange}
                />
                {errors.appointment_date ? (
                  <p className="justo-appointment__modal-error">
                    {errors.appointment_date}
                  </p>
                ) : null}
              </div>
              <div className="justo-appointment__modal-field">
                <label
                  className="justo-appointment__modal-label"
                  htmlFor="appointmentNoShow-time"
                >
                  {strings.time}
                </label>
                <input
                  id="appointmentNoShow-time"
                  type="time"
                  className="justo-appointment__modal-input"
                  value={appointmentTime || ""}
                  onChange={onTimeChange}
                />
                {errors.appointment_time ? (
                  <p className="justo-appointment__modal-error">
                    {errors.appointment_time}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="justo-appointment__modal-field">
            <label
              className="justo-appointment__modal-label"
              htmlFor="appointmentNoShow-remark"
            >
              {strings.noShowDescriptionLabel}
            </label>
            <textarea
              id="appointmentNoShow-remark"
              className="justo-appointment__modal-textarea"
              rows={4}
              placeholder={strings.noShowDescriptionPlaceholder}
              value={comment}
              onChange={onCommentChange}
            />
            {errors.comment ? (
              <p className="justo-appointment__modal-error">{errors.comment}</p>
            ) : null}
          </div>
        </div>
        <div className="justo-appointment__modal-foot">
          <button
            type="button"
            className="justo-appointment__btn justo-appointment__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {strings.cancel}
          </button>
          <button
            type="button"
            className="justo-appointment__btn justo-appointment__btn--danger"
            onClick={onSubmit}
            disabled={submitting}
          >
            <i className="fa fa-check" aria-hidden="true" />
            {submitting ? "Saving…" : strings.noShowConfirm}
          </button>
        </div>
      </div>
    </>
  );
};

const ViewAppointment = () => {
  const navigate = useNavigate();
  const [appointmentData, setAppointmentData] = useState({});
  const params = useParams();
  const { appointment_id = "" } = params || {};
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false); // check-in confirm
  const [showModals, setShowModals] = useState(false); // low score booking
  const [showNotInterestedModal, setShowNotInterestedModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);
  const [leadSourceType, setleadSourceType] = useState([]);
  const [comment, setComment] = useState("");
  const [leadScore, setLeadScore] = useState(0);
  const [selectedReasonId, setSelectedReasonId] = useState("");
  const [noShowStatus, setNoShowStatus] = useState("");
  const [noShowDate, setNoShowDate] = useState("");
  const [noShowTime, setNoShowTime] = useState("");
  const [closingModalErrors, setClosingModalErrors] = useState({});
  const [closingSubmitting, setClosingSubmitting] = useState(false);
  const [showBookNowModal, setShowBookNowModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [closingManagerList, setClosingManagerList] = useState([]);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [siteVisitMapping, setSiteVisitMapping] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [closingStatusBackWarningVisible, setClosingStatusBackWarningVisible] =
    useState(false);
  const [pendingLeaveTarget, setPendingLeaveTarget] = useState(null);
  const allowHistoryLeaveRef = useRef(false);
  const historyTrapDepthRef = useRef(0);

  const closingManagerSelecter = useSelector((state) => state?.cmData);
  const siteVisitSelector = useSelector((state) => state?.siteVisit);

  const openModal = () => {
    setIsModalOpen(true);
    dispatch(
      getClosingManagerList({ property_id: appointmentData?.property_id }),
    );
  };

  // Handle closing the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedManager(""); // Reset selected manager when closing
  };

  // Handle selection change
  const handleManagerChange = (e) => {
    setSelectedManager(e.target.value);
  };

  // Handle submitting the form
  const handleSubmit = () => {
    // You can add the submit logic here
    dispatch(
      allocate_closingManager({
        user_id: selectedManager,
        appointment_id: appointment_id,
      }),
    );
    closeModal();
  };

  // Function to handle opening the modal
  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleShowModals = () => setShowModals(true);
  const handleShowNotInt = () => {
    setComment("");
    setSelectedReasonId("");
    setClosingModalErrors({});
    setShowNotInterestedModal(true);
  };

  const handleShowNoShow = () => {
    setComment("");
    setNoShowStatus("");
    setNoShowDate("");
    setNoShowTime("");
    setClosingModalErrors({});
    setShowNoShowModal(true);
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setShowNotInterestedModal(false);
    setShowNoShowModal(false);
    setShowBookNowModal(false);
    setShowModals(false);
    setComment("");
    setSelectedReasonId("");
    setNoShowStatus("");
    setNoShowDate("");
    setNoShowTime("");
    setClosingModalErrors({});
    setClosingSubmitting(false);
  };

  useEffect(() => {
    if (
      !showModal &&
      !showModals &&
      !showNotInterestedModal &&
      !showNoShowModal
    ) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        handleCloseModal();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showModal, showModals, showNotInterestedModal, showNoShowModal]);

  const handleUpdateVisitorFromScore = () => {
    if (appointmentData?._id) {
      localStorage.setItem("appointment_id", appointmentData._id);
    }
    setShowModals(false);
    navigatpage(`/visitor_form?lead_id=${appointmentData.lead_id}`);
  };

  const leadSourceSelector = useSelector((state) => state.masterData);
  const appointmentDetailSelector = useSelector((state) => state.appointment);

  const allocateAppointmentSelector = useSelector(
    (state) => state.allocatedClosing,
  );

  const visitorDetailSelector = useSelector((state) => state.visitor);
  const navigatpage = async (navname) => {
    navigate(navname);
  };

  const { response: res } = useSelector((state) => state.login);

  const role_id = res?.data?.role_id;
  const CtTeam = ClosingTL === role_id;
  const misRole = MIS_RoleId === res?.data?.role_id;

  const CMTeam =
    ClosingTL === role_id ||
    ClosingManager === role_id ||
    closing_head_id === role_id;

  const SMteam =
    SourcingTL === role_id ||
    SourcingManager === role_id ||
    sourcing_head_id === role_id;

  const mixTeam =
    SCM === role_id ||
    SiteHead === role_id ||
    ClusterHead === role_id ||
    BusinessHead === role_id ||
    admin_id === role_id;

  const canSyncDisposition =
    (ClosingManager === role_id ||
      SCM === role_id ||
      admin_id === role_id ||
      AdminRoleId === role_id) &&
    appointmentData?.needs_disposition_sync === true;

  const handleSyncDispositionReport = async () => {
    const aptId = appointmentData?._id || appointment_id;
    if (!aptId) {
      errorToast("Appointment not found.");
      return;
    }
    try {
      const res = await visitorService.syncDispositionReport({
        appointment_id: aptId,
      });
      const response = res?.data || res;
      if (response?.status === 200) {
        successToast(
          response?.already_synced
            ? "Disposition entry already exists."
            : "Disposition report entry synced successfully.",
        );
        dispatch(getAppointmentDetail({ appointment_id }));
      } else {
        errorToast(response?.message || "Sync failed.");
      }
    } catch (err) {
      errorToast(err?.message || "Sync failed.");
    }
  };

  const { edit, status, create, approve, isAdmin } = usePermission({
    edit: "close_appointment",
    status: "appointment_status_update",
    create: "book_now",
    approve: "ready_for_booking",
  });

  useEffect(() => {
    dispatch(visitorDetail({ lead_id: appointmentData.lead_id }));
  }, [dispatch, appointmentData]);

  useEffect(() => {
    dispatch(getAppointmentDetail({ appointment_id }));
    dispatch(getMasterList({ type: 8 }));
  }, [dispatch]);

  useEffect(() => {
    if (visitorDetailSelector.response) {
      const { response } = visitorDetailSelector;
      if (response?.status === 200) {
        setLeadScore(response?.data[0]?.lead_score);
      }
    }
  }, [visitorDetailSelector]);

  useEffect(() => {
    if (leadSourceSelector.response) {
      const { response } = leadSourceSelector;
      if (response?.status === 200) {
        setleadSourceType(handleListResponse(response.data));
      } else {
        setleadSourceType([]);
      }
    }
  }, [leadSourceSelector]);

  useEffect(() => {
    if (allocateAppointmentSelector.response) {
      const { response } = allocateAppointmentSelector;
      if (response?.status === 200) {
        successToast(response?.message);
        dispatch(getAppointmentDetail({ appointment_id }));
        dispatch(remove_allocate());
      } else {
        errorToast(response?.message);
      }
    }
  }, [allocateAppointmentSelector, appointment_id, dispatch]);

  useEffect(() => {
    if (appointmentDetailSelector.response) {
      const { response } = appointmentDetailSelector;
      if (response?.status === 200) {
        setAppointmentData(handleDetailResponse(response?.data));
      } else {
        setAppointmentData({});
      }
    }
  }, [appointmentDetailSelector]);

  // Fetch site visit mapping for LMS integration (optional - gracefully handles missing backend)
  useEffect(() => {
    if (appointment_id) {
      // Try with appointment_id first, backend will fallback to lead_id if needed
      // LMS Integration: Fetch site visit mapping data
      // Note: This will fail silently if LMS backend is not deployed yet
      dispatch(getSiteVisitStatusData({ appointment_id }))
        .then((res) => {
          if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
            // LMS Integration: Log incoming site visit mapping data
            setSiteVisitMapping(res.data[0]);
          } else {
            setSiteVisitMapping(null);
          }
        })
        .catch((err) => {
          // Silently handle missing LMS backend - this is expected if backend is not deployed yet
          setSiteVisitMapping(null);
        });
    }
  }, [appointmentData, appointment_id, dispatch]);

  const assign_role =
    appointmentData?.assign_appointment?.length > 0
      ? appointmentData?.assign_appointment[0]?.assign_by_role
      : "";

  const handleCheckin = () => {
    if (!appointmentData?._id) {
      handleCloseModal();
      return;
    }
    dispatch(checkinAppointment({ appointment_id: appointmentData._id }))
      .then((result) => {
        if (!result || Number(result.status) !== 200) {
          return;
        }
        if (siteVisitMapping?.jv_site_visit_id) {
          const payload = {
            jvSiteVisitId: siteVisitMapping.jv_site_visit_id,
            status: "Visited",
            checkInTime: new Date().toISOString(),
            role: res?.data?.role_title || "auto",
          };

          dispatch(updateSiteVisitStatus(payload)).catch(() => {});
        }
        dispatch(getAppointmentDetail({ appointment_id }));
      })
      .catch(() => {});
    handleCloseModal();
  };

  const validateClosingForm = () => {
    const errors = {};
    if (!String(comment || "").trim()) {
      errors.comment = strings.closingRemarkRequired;
    }
    setClosingModalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateNoShowForm = () => {
    const errors = {};
    if (!String(noShowStatus || "").trim()) {
      errors.status = strings.noShowStatusRequired;
    }
    if (String(noShowStatus) === "1") {
      if (!String(noShowDate || "").trim()) {
        errors.appointment_date = strings.noShowDateRequired;
      }
      if (!String(noShowTime || "").trim()) {
        errors.appointment_time = strings.noShowTimeRequired;
      }
    }
    if (!String(comment || "").trim()) {
      errors.comment = strings.noShowDescriptionRequired;
    }
    setClosingModalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const notifyLmsClosing = (closingStatus) => {
    if (!siteVisitMapping?.jv_site_visit_id) {
      handleCloseModal();
      dispatch(getAppointmentDetail({ appointment_id }));
      return;
    }
    const payload = {
      jvSiteVisitId: siteVisitMapping.jv_site_visit_id,
      closingStatus,
      status: closingStatus === "Visit Cancelled" ? "Not Visited" : undefined,
      role: res?.data?.role_title || "auto",
    };
    dispatch(updateSiteVisitStatus(payload)).finally(() => {
      handleCloseModal();
      dispatch(getAppointmentDetail({ appointment_id }));
    });
  };

  const handleSubmitNoShow = () => {
    if (!appointmentData?._id) return;
    if (!validateNoShowForm()) return;

    setClosingSubmitting(true);
    const remark = String(comment || "").trim();
    const existingDate = appointmentData?.appointment_date
      ? moment(appointmentData.appointment_date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    const existingTime = appointmentData?.appointment_time || "12:00 PM";

    if (String(noShowStatus) === "1") {
      const displayTime = noShowTime
        ? moment(`2000-01-01 ${noShowTime}`).format("hh:mm A")
        : existingTime;
      dispatch(
        addAppointment({
          appointment_id,
          lead_id: appointmentData.lead_id,
          property_id: appointmentData.property_id,
          status: 1,
          appointment_date: noShowDate,
          appointment_time: displayTime,
          remark,
          update_type: 2,
          type: 1,
        }),
      )
        .then((result) => {
          if (!result) {
            setClosingSubmitting(false);
            return;
          }
          handleCloseModal();
          dispatch(getAppointmentDetail({ appointment_id }));
        })
        .catch(() => {
          setClosingSubmitting(false);
        });
      return;
    }

    dispatch(
      editAppointment({
        appointment_id,
        status: 4,
        remark,
        appointment_date: existingDate,
        appointment_time: existingTime,
      }),
    )
      .then((result) => {
        if (!result) {
          setClosingSubmitting(false);
          return;
        }
        notifyLmsClosing("Visit Cancelled");
      })
      .catch(() => {
        setClosingSubmitting(false);
      });
  };

  const handleSubmitClosingStatus = (appointmentStatus) => {
    if (!appointmentData?._id) return;
    if (!validateClosingForm()) return;

    setClosingSubmitting(true);
    dispatch(
      close_appointment({
        lead_id: appointmentData.lead_id,
        appointment_id,
        appointment_status: appointmentStatus,
        resion: selectedReasonId || "",
        comment: String(comment || "").trim(),
        deposition_source: "appointment_details",
      }),
    )
      .then((result) => {
        if (!result) {
          setClosingSubmitting(false);
          return;
        }
        notifyLmsClosing("Not Interested");
      })
      .catch(() => {
        setClosingSubmitting(false);
      });
  };

  // Handle webhook trigger
  const handleTriggerWebhook = () => {
    if (siteVisitMapping?.jv_site_visit_id) {
      setWebhookLoading(true);
      dispatch(
        triggerWebhook({ jv_site_visit_id: siteVisitMapping.jv_site_visit_id }),
      )
        .then((res) => {
          if (res) {
            setShowWebhookModal(false);
            // Refresh appointment details
            dispatch(getAppointmentDetail({ appointment_id }));
          }
          setWebhookLoading(false);
        })
        .catch((err) => {
          // Error handled by Redux action
          setWebhookLoading(false);
        });
    } else {
      errorToast("Site visit mapping not found");
    }
  };

  const handleShowWebhookModal = () => {
    setShowWebhookModal(true);
  };

  const handleCloseWebhookModal = () => {
    setShowWebhookModal(false);
  };

  useEffect(() => {
    const { response = null, create = false } = closingManagerSelecter;
    if (response && response?.status === 200) {
      if (!create && response.data?.length > 0) {
        setClosingManagerList(
          handleListResponse(response?.data) ? response?.data : [],
        );
      }
    } else {
      setClosingManagerList([]);
    }
  }, [closingManagerSelecter]);

  useEffect(() => {
    seo({
      title: "Appointment Details",
      metaDescription: "Appoinment details",
      canonicalLink: window.location.href,
    });
  }, []);

  const viewStatusItem = {
    ...appointmentData,
    closing_status:
      appointmentData?.closing_status || siteVisitMapping?.closing_status || "",
    site_visit_status:
      appointmentData?.site_visit_status || siteVisitMapping?.status || "",
  };
  const viewCheckInDisplay = resolveCheckInDisplay(viewStatusItem);
  const viewClosingLabel = resolveClosingStatusLabel(viewStatusItem);
  const viewHasCheckin = viewCheckInDisplay.showAsVisited;
  const viewCheckInLabel = viewCheckInDisplay.label;
  const appointmentActions = resolveAppointmentActionButtons(viewStatusItem);
  const showPostCheckinButtons = appointmentActions.showPostCheckin;
  const showNoShowCheckin = appointmentActions.showNoShowCheckin;
  const closingReasonOptions = (leadSourceType || []).map((item) => ({
    value: String(item._id),
    label: item.title,
  }));
  const selectedClosingReason =
    closingReasonOptions.find((opt) => opt.value === String(selectedReasonId)) ||
    null;

  const leaveAppointmentDetails = (target = "/appointment") => {
    navigate(target == null ? "/appointment" : target);
  };

  const requestLeaveAppointmentDetails = (target = "/appointment") => {
    if (!shouldWarnClosingStatusOnBack(viewStatusItem)) {
      leaveAppointmentDetails(target);
      return;
    }
    setPendingLeaveTarget(target);
    setClosingStatusBackWarningVisible(true);
  };

  const handleStayOnClosingStatusWarning = () => {
    setClosingStatusBackWarningVisible(false);
    setPendingLeaveTarget(null);
  };

  const handleGoBackFromClosingStatusWarning = () => {
    const target =
      pendingLeaveTarget == null ? "/appointment" : pendingLeaveTarget;
    setClosingStatusBackWarningVisible(false);
    setPendingLeaveTarget(null);
    allowHistoryLeaveRef.current = true;

    if (target === BROWSER_HISTORY_BACK) {
      const trapDepth = historyTrapDepthRef.current;
      historyTrapDepthRef.current = 0;
      if (trapDepth > 0) {
        window.history.go(-(trapDepth + 1));
      } else {
        leaveAppointmentDetails(-1);
      }
      return;
    }

    leaveAppointmentDetails(target);
  };

  const shouldWarnOnLeave = shouldWarnClosingStatusOnBack(viewStatusItem);

  // Match Android hardware back: intercept browser back and show the same warning modal.
  useEffect(() => {
    if (!shouldWarnOnLeave) {
      historyTrapDepthRef.current = 0;
      allowHistoryLeaveRef.current = false;
      return undefined;
    }

    allowHistoryLeaveRef.current = false;
    historyTrapDepthRef.current = 0;

    const trapHistory = () => {
      window.history.pushState(
        { __appointmentDetailsTrap: true },
        "",
        window.location.href,
      );
      historyTrapDepthRef.current += 1;
    };

    trapHistory();

    const onPopState = () => {
      if (allowHistoryLeaveRef.current) {
        return;
      }
      trapHistory();
      setPendingLeaveTarget(BROWSER_HISTORY_BACK);
      setClosingStatusBackWarningVisible(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      historyTrapDepthRef.current = 0;
    };
  }, [shouldWarnOnLeave, appointment_id]);
  const roleId = String(res?.data?.role_id || "");
  const canBookNowRole =
    roleId === String(ClosingManager) ||
    roleId === String(ClosingTL) ||
    roleId === String(CSTL) ||
    roleId === String(closing_head_id) ||
    roleId === String(SourcingHead) ||
    roleId === String(ClusterHead) ||
    roleId === String(SiteHead) ||
    roleId === String(SCM) ||
    roleId === String(AdminRoleId) ||
    roleId === String(admin_id);
  const showBookNow =
    Boolean(create) &&
    !misRole &&
    canBookNowRole &&
    shouldShowPostCheckinBookNow(viewStatusItem);

  const visitorFullName = (() => {
    const firstName = appointmentData?.customer_first_name || "";
    const lastName = appointmentData?.customer_last_name || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || strings.notavailable;
  })();

  const visitDateTime = appointmentData?.appointment_date
    ? `${moment(appointmentData.appointment_date).format("YYYY-MM-DD")} & ${appointmentData?.appointment_time || ""}`
    : strings.notavailable;

  // Same as app: prefer latest follow-up remark; fall back to appointment (walk-in / revisit) remark.
  const displayRemark =
    String(appointmentData?.latest_followup_remark || "").trim() ||
    appointmentData?.remark;

  const budgetValue = appointmentData?.budget_amount
    ? appointmentData.budget_amount
    : appointmentData?.min_budget && appointmentData?.max_budget
      ? `${appointmentData.min_budget}${appointmentData.min_budget_type} - ${appointmentData.max_budget}${appointmentData.max_budget_type}`
      : strings.notavailable;

  const possessionValue = appointmentData?.expected_possession_period
    ? appointmentData.expected_possession_period
    : appointmentData?.expected_possession_date === "" ||
        appointmentData?.expected_possession_date === undefined ||
        appointmentData?.expected_possession_date === null
      ? strings.notavailable
      : moment.utc(appointmentData.expected_possession_date).format("DD-MM-YYYY");

  const leadSourceDisplay = appointmentData?.lead_source
    ? appointmentData.lead_source == "Reference" &&
      appointmentData.referrel_partner === 1
      ? "Referral Partner"
      : appointmentData.lead_source
    : strings.notfount;

  const assignedCm =
    appointmentData?.assign_appointment?.[0]?.user_name || strings.notavailable;

  return (
    <div className="page-body justo-appointment-page">
      <Breadcrumb
        title={strings.viewAppointmentDetails}
        url={"/appointment"}
        parentListing={strings.appointmentList}
        titleactive={strings.viewappointmentdetails}
        isBackBtn={true}
        onBackClick={(target) => requestLeaveAppointmentDetails(target)}
        onParentClick={(target) =>
          requestLeaveAppointmentDetails(target || "/appointment")
        }
      />
      <div className="container-fluid justo-appointment">
        <div className="justo-appointment__card justo-appointment-detail">
          <div className="justo-appointment-detail__hero">
            <div className="justo-appointment-detail__hero-main">
              <h1 className="justo-appointment-detail__property">
                {displayValue(appointmentData?.property_title)}
              </h1>
              <p className="justo-appointment-detail__meta">
                <span>
                  <strong>{visitorFullName}</strong>
                </span>
                <span>{visitDateTime}</span>
                <span>{assignedCm}</span>
              </p>
            </div>
            <div className="justo-appointment-detail__hero-actions">
              <StatusBadge
                label={viewClosingLabel}
                badgeClass={getClosingStatusBadgeClass(viewClosingLabel)}
              />
              {viewCheckInLabel ? (
                <CheckInStatusBadge
                  showAsVisited={viewHasCheckin}
                  label={viewCheckInLabel}
                />
              ) : null}
              {CtTeam &&
                (appointmentData?.status === 1 ||
                  appointmentData?.status === 10) && (
                  <button
                    type="button"
                    className="justo-appointment__btn justo-appointment__btn--solid"
                    onClick={openModal}
                  >
                    Allocate
                  </button>
                )}
            </div>
          </div>

          <div className="justo-appointment-detail__stats">
            <div className="justo-appointment-detail__stat">
              <span className="justo-appointment-detail__stat-label">
                {strings.visitorScore}
              </span>
              <span className="justo-appointment-detail__stat-value">
                {displayValue(appointmentData?.lead_score)}
              </span>
            </div>
            <div className="justo-appointment-detail__stat">
              <span className="justo-appointment-detail__stat-label">
                {strings.budget}
              </span>
              <span
                className={`justo-appointment-detail__stat-value${
                  budgetValue === strings.notavailable ? " is-muted" : ""
                }`}
              >
                {budgetValue}
              </span>
            </div>
            <div className="justo-appointment-detail__stat">
              <span className="justo-appointment-detail__stat-label">
                {strings.configuration}
              </span>
              <span
                className={`justo-appointment-detail__stat-value${
                  !appointmentData?.configuration ? " is-muted" : ""
                }`}
              >
                {displayValue(appointmentData?.configuration)}
              </span>
            </div>
            <div className="justo-appointment-detail__stat">
              <span className="justo-appointment-detail__stat-label">
                {strings.noOfGuest}
              </span>
              <span
                className={`justo-appointment-detail__stat-value${
                  !appointmentData?.number_of_guest ? " is-muted" : ""
                }`}
              >
                {displayValue(appointmentData?.number_of_guest)}
              </span>
            </div>
          </div>

          <div className="justo-appointment-detail__content">
            <div className="justo-appointment-detail__grid">
              <FactSection title="Visit">
                <FactCell label={strings.siteVisitTimeDate} value={visitDateTime} />
                <FactCell
                  label={strings.expectedPossession}
                  value={possessionValue}
                />
                <FactCell label={strings.closingStatus}>
                  <StatusBadge
                    label={viewClosingLabel}
                    badgeClass={getClosingStatusBadgeClass(viewClosingLabel)}
                  />
                </FactCell>
                <FactCell label={strings.assignTo} value={assignedCm} />
                {viewCheckInLabel ? (
                  <FactCell label={strings.checkInStatus}>
                    <CheckInStatusBadge
                      showAsVisited={viewHasCheckin}
                      label={viewCheckInLabel}
                    />
                  </FactCell>
                ) : null}
                <FactCell label={strings.reason} value={appointmentData?.resion} />
                <FactCell label={strings.remarkField} value={displayRemark} />
              </FactSection>

              <FactSection title="Lead">
                <FactCell label={strings.leadSource}>
                  {`${leadSourceDisplay} ${getCPLeadType(appointmentData?.cp_lead_type)}`.trim()}
                </FactCell>
                {appointmentData?.lead_source == "Reference" && (
                  <FactCell
                    label={strings.referrer}
                    value={appointmentData?.referrer_name}
                  />
                )}
                {appointmentData?.lead_source == "Channel Partner" && (
                  <FactCell
                    label={
                      appointmentData.cp_type === 2
                        ? "CP Company Name"
                        : "CP Name"
                    }
                    value={appointmentData?.cp_name}
                  />
                )}
                {(Number(appointmentData?.cp_type) === 2 ||
                  (appointmentData?.cp_emp_name &&
                    String(appointmentData.cp_emp_name).trim() !== "")) && (
                  <FactCell
                    label={strings.emname}
                    value={appointmentData?.cp_emp_name}
                  />
                )}
                <FactCell label={strings.created} value={appointmentData?.create_by} />
                <FactCell
                  label={strings.createdByRole}
                  value={
                    appointmentData?.created_by_role === "" ||
                    appointmentData?.created_by_role === undefined ||
                    appointmentData?.created_by_role === null
                      ? strings.notfount
                      : appointmentData?.created_by_role
                  }
                />
              </FactSection>

              <FactSection title="Logistics">
                <FactCell label={strings.pickup} value={appointmentData?.pickup} />
                <FactCell
                  label={strings.pickupLocation}
                  value={appointmentData?.pickup_location}
                />
                <FactCell
                  label={strings.noOfGuest}
                  value={appointmentData?.number_of_guest}
                />
              </FactSection>
            </div>
          </div>

          <div className="justo-appointment-detail__actions">
            {canSyncDisposition ? (
              <div className="justo-appointment-detail__sync-block">
                <p className="justo-appointment-detail__sync-msg">
                  Disposition report entry is missing for this visitor. Please sync before proceeding.
                </p>
                <button
                  className="justo-appointment__btn justo-appointment__btn--outline"
                  type="button"
                  onClick={handleSyncDispositionReport}
                >
                  Sync to Disposition Report
                </button>
              </div>
            ) : (
            <>
            {CMTeam && (
              <>
                <button
                  className="justo-appointment__btn justo-appointment__btn--outline"
                  type="button"
                  onClick={() => {
                    navigatpage(
                      `/visitor_form?lead_id=${appointmentData.lead_id}`,
                    );
                    localStorage.setItem(
                      "appointment_id",
                      appointmentData._id,
                    );
                  }}
                >
                  {strings.updateVisitordetails}
                </button>
                <button
                  className="justo-appointment__btn justo-appointment__btn--outline"
                  type="button"
                  onClick={() =>
                    navigatpage(`/allfollowup/${appointmentData.lead_id}`)
                  }
                >
                  {strings.viewVisitorHistory}
                </button>
              </>
            )}
            {showPostCheckinButtons ? (
              <>
                    {edit && !misRole && (
                      <button
                        className="justo-appointment__btn justo-appointment__btn--danger"
                        type="button"
                        onClick={handleShowNotInt}
                      >
                        <i className="fa fa-ban" aria-hidden="true" />
                        {strings.notInterested}
                      </button>
                    )}
                    {status && !SMteam && !misRole && (
                      <button
                        className="justo-appointment__btn justo-appointment__btn--revisit"
                        type="button"
                        onClick={() => {
                          navigatpage(`/revisit/${appointmentData?._id}`);
                        }}
                      >
                        <i className="fa fa-refresh" aria-hidden="true" />
                        {strings.revisit}
                      </button>
                    )}
                    {!misRole && !SMteam && (
                      <button
                        className="justo-appointment__btn justo-appointment__btn--outline"
                        type="button"
                        onClick={() => {
                          const url = appointment_id
                            ? `/updatestatusfollow/${appointmentData.lead_id}/${appointment_id}`
                            : `/updatestatusfollow/${appointmentData.lead_id}`;
                          navigatpage(url);
                        }}
                      >
                        <i className="fa fa-phone" aria-hidden="true" />
                        {strings.followUp}
                      </button>
                    )}
                {showBookNow && (
                    <button
                      className="justo-appointment__btn justo-appointment__btn--success"
                      type="button"
                      onClick={() => {
                        if (leadScore > 70) {
                          navigate(
                            `/addBooking/${appointmentData.lead_id}/${appointment_id}`,
                          );
                        } else {
                          handleShowModals();
                        }
                      }}
                    >
                      <i className="fa fa-bookmark" aria-hidden="true" />
                      {strings.bookNow}
                    </button>
                  )}
              </>
            ) : showNoShowCheckin ? (
              res?.data?.role_title === "SCM" ? (
                assign_role === "SCM" && (
                  <>
                    {!misRole && (
                      <button
                        className="justo-appointment__btn justo-appointment__btn--warning"
                        type="button"
                        onClick={handleShowNoShow}
                      >
                        <i className="fa fa-clock-o" aria-hidden="true" />
                        {strings.noShow}
                      </button>
                    )}
                    <button
                      className="justo-appointment__btn justo-appointment__btn--success"
                      type="button"
                      onClick={handleShowModal}
                    >
                      <i className="fa fa-check" aria-hidden="true" />
                      {strings.visitorCheckin}
                    </button>
                  </>
                )
              ) : (
                <>
                  {!misRole && (
                    <button
                      className={`justo-appointment__btn ${
                        SMteam
                          ? "justo-appointment__btn--solid"
                          : "justo-appointment__btn--warning"
                      }`}
                      type="button"
                      onClick={
                        SMteam
                          ? () =>
                              navigatpage(
                                `/update_appointment_status/${appointmentData?._id}`,
                              )
                          : handleShowNoShow
                      }
                    >
                      {SMteam ? (
                        <>
                          <i className="fa fa-check" aria-hidden="true" />
                          {strings.statusUpdate}
                        </>
                      ) : (
                        <>
                          <i className="fa fa-clock-o" aria-hidden="true" />
                          {strings.noShow}
                        </>
                      )}
                    </button>
                  )}
                  {!SMteam && !misRole && (
                    <button
                      className="justo-appointment__btn justo-appointment__btn--success"
                      type="button"
                      onClick={handleShowModal}
                    >
                      <i className="fa fa-check" aria-hidden="true" />
                      {strings.visitorCheckin}
                    </button>
                  )}
                </>
              )
            ) : null}
            </>
            )}
          </div>
        </div>
      </div>
      <NoShowFormModal
        open={showNoShowModal}
        statusValue={noShowStatus}
        onStatusChange={(option) => {
          const next = option?.value ? String(option.value) : "";
          setNoShowStatus(next);
          setNoShowDate("");
          setNoShowTime("");
          if (closingModalErrors.status) {
            setClosingModalErrors((prev) => ({ ...prev, status: "" }));
          }
        }}
        appointmentDate={noShowDate}
        onDateChange={(e) => {
          setNoShowDate(e.target.value);
          if (closingModalErrors.appointment_date) {
            setClosingModalErrors((prev) => ({
              ...prev,
              appointment_date: "",
            }));
          }
        }}
        appointmentTime={noShowTime}
        onTimeChange={(e) => {
          setNoShowTime(e.target.value);
          if (closingModalErrors.appointment_time) {
            setClosingModalErrors((prev) => ({
              ...prev,
              appointment_time: "",
            }));
          }
        }}
        comment={comment}
        onCommentChange={(e) => {
          setComment(e.target.value);
          if (closingModalErrors.comment) {
            setClosingModalErrors((prev) => ({ ...prev, comment: "" }));
          }
        }}
        errors={closingModalErrors}
        submitting={closingSubmitting}
        onClose={handleCloseModal}
        onSubmit={handleSubmitNoShow}
      />
      <ClosingStatusFormModal
        open={showNotInterestedModal}
        titleId="appointmentNotInterestedTitle"
        title={strings.notInterestedTitle}
        subtitle={strings.notInterestedSubtitle}
        reasonOptions={closingReasonOptions}
        selectedReason={selectedClosingReason}
        onReasonChange={(option) =>
          setSelectedReasonId(option?.value ? String(option.value) : "")
        }
        comment={comment}
        onCommentChange={(e) => {
          setComment(e.target.value);
          if (closingModalErrors.comment) {
            setClosingModalErrors((prev) => ({ ...prev, comment: "" }));
          }
        }}
        errors={closingModalErrors}
        submitting={closingSubmitting}
        confirmLabel={strings.notInterestedConfirm}
        confirmDanger
        onClose={handleCloseModal}
        onSubmit={() => handleSubmitClosingStatus(6)}
      />
      {closingStatusBackWarningVisible && (
        <div
          className="modal fade show d-block justo-confirm-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="closingStatusBackWarningTitle"
          onClick={handleStayOnClosingStatusWarning}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <button
                className="justo-confirm-modal__close"
                type="button"
                onClick={handleStayOnClosingStatusWarning}
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
              <div className="modal-body justo-confirm-modal__body">
                <h5
                  className="justo-confirm-modal__title"
                  id="closingStatusBackWarningTitle"
                >
                  {strings.closingStatusBackWarningTitle}
                </h5>
                <p className="justo-confirm-modal__text">
                  {strings.closingStatusBackWarningBody}
                </p>
              </div>
              <div className="modal-footer justo-confirm-modal__footer">
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--ghost"
                  type="button"
                  onClick={handleStayOnClosingStatusWarning}
                >
                  {strings.stayOnThisScreen}
                </button>
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--danger"
                  type="button"
                  onClick={handleGoBackFromClosingStatusWarning}
                >
                  {strings.goBackWithoutClosingStatus}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && (
        <div
          className="modal fade show d-block justo-confirm-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmCheckinTitle"
          onClick={handleCloseModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <button
                className="justo-confirm-modal__close"
                type="button"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
              <div className="modal-body justo-confirm-modal__body">
                <h5 className="justo-confirm-modal__title" id="confirmCheckinTitle">
                  {strings.confirmCheckinTitle}
                </h5>
                <p className="justo-confirm-modal__text">{strings.confirmCheckin}</p>
              </div>
              <div className="modal-footer justo-confirm-modal__footer">
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--ghost"
                  type="button"
                  onClick={handleCloseModal}
                >
                  {strings.cancel}
                </button>
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--success"
                  type="button"
                  onClick={handleCheckin}
                >
                  <i className="fa fa-check" aria-hidden="true" />
                  {strings.visitorCheckin}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showModals && (
        <div
          className="modal fade show d-block justo-confirm-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lowVisitorScoreTitle"
          onClick={handleCloseModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <button
                className="justo-confirm-modal__close"
                type="button"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
              <div className="modal-body justo-confirm-modal__body">
                <h5 className="justo-confirm-modal__title" id="lowVisitorScoreTitle">
                  {strings.lowVisitorScoreTitle}
                </h5>
                <p className="justo-confirm-modal__text">
                  {strings.lowVisitorScoreMessage}
                </p>
              </div>
              <div className="modal-footer justo-confirm-modal__footer">
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--ghost"
                  type="button"
                  onClick={handleCloseModal}
                >
                  {strings.cancel}
                </button>
                <button
                  className="justo-confirm-modal__btn justo-confirm-modal__btn--solid"
                  type="button"
                  onClick={handleUpdateVisitorFromScore}
                >
                  <i className="fa fa-user" aria-hidden="true" />
                  {strings.updateVisitordetails}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div
          className="modal"
          tabIndex="-1"
          style={{
            display: "block",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent background
            backdropFilter: "blur(5px)", // Apply the blur effect to the background
            zIndex: 1050, // Ensure the modal is above other content
          }}
        >
          <div
            className="modal-dialog"
            style={{
              position: "relative",
              margin: "10% auto",
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              maxWidth: "500px",
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{strings.allocate}</h5>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="closingManager">
                    {strings.selectNewClosingManager}
                  </label>
                  <select
                    id="closingManager"
                    className="form-control"
                    value={selectedManager}
                    onChange={handleManagerChange}
                  >
                    <option value="">Select</option>
                    {/* Map through closingManagerList */}
                    {closingManagerList.map((manager) => (
                      <option key={manager.user_id} value={manager.user_id}>
                        {manager.user_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ViewAppointment;
